/* UGC video row — the only behaviour the markup can't express on its own.
 *
 * Native (no JS): horizontal snap carousel, the <dialog> top layer + backdrop +
 * Esc + close button (form method="dialog"), vertical slide navigation by
 * wheel / swipe (scroll-snap), page scroll lock (html:has(dialog[open])).
 *
 * JS below: play only what's on screen, the auto-advance tour, mute /
 * play-pause / share, rail sync, and skipping videos the browser can't decode.
 */
(function () {
  'use strict';

  function noop() {}

  function boot(scope) {
    (scope || document).querySelectorAll('[data-ha-ugc]').forEach(init);
  }

  function init(root) {
    if (root.dataset.haReady) return;
    root.dataset.haReady = '1';

    var track = root.querySelector('.ha-ugc__track');
    var modal = root.querySelector('.ha-ugcm');
    if (!track || !modal) return;

    /* ------------------------------------------------------------ carousel */
    var previews = [].slice.call(root.querySelectorAll('.ha-ugc__preview'));

    if ('IntersectionObserver' in window) {
      var pio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            var p = v.play();
            if (p && p.catch) p.catch(noop);
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { rootMargin: '100px 0px', threshold: 0.01 });
      previews.forEach(function (v) { pio.observe(v); });
    } else {
      previews.forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(noop); });
    }

    previews.forEach(function (v) {
      function drop() {
        var card = v.closest('.ha-ugc__card');
        if (card) card.hidden = true;
      }
      v.addEventListener('error', drop);
      v.addEventListener('loadeddata', function () { if (!v.videoWidth) drop(); });
    });

    // auto-advance tour: cycles the strip until the shopper takes it over
    var tourMs = parseInt(root.dataset.haTour, 10) || 0;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var tour = null;

    function stopTour() {
      clearInterval(tour);
      tour = null;
    }

    function tourStep() {
      var cards = [].slice.call(track.children).filter(function (c) { return !c.hidden; });
      if (cards.length < 2) return stopTour();
      var i = 0;
      while (i < cards.length - 1 && cards[i].offsetLeft < track.scrollLeft - 4) i++;
      var next = cards[(i + 1) % cards.length];
      track.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
    }

    if (tourMs && !reduce) tour = setInterval(tourStep, tourMs);

    // only horizontal intent takes over — scrolling the page past the row must not
    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stopTour();
    }, { passive: true });

    var touch = null;
    track.addEventListener('touchstart', function (e) {
      touch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      if (!touch) return;
      var dx = e.touches[0].clientX - touch.x;
      var dy = e.touches[0].clientY - touch.y;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) { stopTour(); touch = null; }
    }, { passive: true });

    // desktop drag-to-scroll (an overflow strip has no mouse gesture of its own)
    var drag = null;
    var dragged = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: track.scrollLeft };
      dragged = false;
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!dragged && Math.abs(dx) > 6) {
        dragged = true;
        track.classList.add('ha-dragging');
        stopTour();
      }
      if (dragged) track.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!drag) return;
      drag = null;
      track.classList.remove('ha-dragging');
      if (dragged) setTimeout(function () { dragged = false; }, 0);
    });
    // a drag must not count as the click that opens the player
    track.addEventListener('click', function (e) {
      if (dragged) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* -------------------------------------------------------------- player */
    var slides = modal.querySelector('.ha-ugcm__slides');
    var slideEls = [].slice.call(slides.children);
    var boxes = [].slice.call(modal.querySelectorAll('.ha-ugcm__box'));
    var boxwrap = modal.querySelector('.ha-ugcm__boxwrap');
    var muteBtn = modal.querySelector('[data-ha-mute]');
    var toastEl = modal.querySelector('.ha-ugcm__toast');
    var desktop = !window.matchMedia || window.matchMedia('(min-width: 750px)').matches;
    var cur = -1;
    var muted = false;

    function main(i) { return slideEls[i] && slideEls[i].querySelector('.ha-ugcm__video'); }

    function load(i, eager) {
      var slide = slideEls[i];
      if (!slide) return;
      slide.querySelectorAll('video[data-src]').forEach(function (v) {
        if (v.classList.contains('ha-ugcm__amb') && !desktop) return;
        v.src = v.dataset.src;
        v.removeAttribute('data-src');
      });
      var v = main(i);
      if (v && eager) v.preload = 'auto';
    }

    function play(v) {
      var p = v.play();
      if (p && p.catch) {
        p.catch(function (err) {
          if (err && err.name === 'NotAllowedError') {
            setMuted(true);
            toast('Tap to unmute', 2500);
            v.play().catch(noop);
          }
        });
      }
    }

    function toast(msg, ms) {
      if (!toastEl) return;
      toastEl.textContent = msg;
      toastEl.classList.add('ha-show');
      clearTimeout(toastEl.haTimer);
      toastEl.haTimer = setTimeout(function () { toastEl.classList.remove('ha-show'); }, ms || 1600);
    }

    function setMuted(state) {
      muted = state;
      slideEls.forEach(function (s) {
        var v = s.querySelector('.ha-ugcm__video');
        if (v) v.muted = state;
      });
      modal.classList.toggle('ha-ugcm--muted', state);
      if (muteBtn) muteBtn.setAttribute('aria-label', state ? 'Unmute video' : 'Mute video');
    }

    function setCurrent(i) {
      if (i === cur || !slideEls[i]) return;
      if (slideEls[cur]) {
        slideEls[cur].querySelectorAll('video').forEach(function (v) { if (!v.paused) v.pause(); });
      }
      cur = i;
      load(i, true);
      load(i + 1); // buffer the next full video ahead of the swipe
      var v = main(i);
      if (v) { v.muted = muted; play(v); }
      var amb = slideEls[i].querySelector('.ha-ugcm__amb');
      if (amb && desktop) { amb.muted = true; var p = amb.play(); if (p && p.catch) p.catch(noop); }
      boxes.forEach(function (b, k) { b.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      var box = boxes[i];
      if (box && boxwrap) {
        boxwrap.scrollTo({
          top: box.offsetTop - (boxwrap.clientHeight - box.offsetHeight) / 2,
          behavior: 'smooth'
        });
      }
    }

    function goTo(i, behavior) {
      var n = slideEls.length;
      if (!n) return;
      // keep scanning the way we were headed, so a dropped slide is stepped over
      var dir = i < cur ? -1 : 1;
      for (var k = 0; k < n; k++) {
        var j = (((i + k * dir) % n) + n) % n;
        if (!slideEls[j].hidden) {
          slides.scrollTo({ top: slideEls[j].offsetTop, behavior: behavior || 'auto' });
          return;
        }
      }
    }

    if ('IntersectionObserver' in window) {
      var sio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setCurrent(slideEls.indexOf(entry.target));
        });
      }, { root: slides, threshold: 0.6 });
      slideEls.forEach(function (s) { sio.observe(s); });
    }

    root.querySelectorAll('[data-ha-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.dataset.haOpen, 10) || 0;
        modal.showModal();
        setMuted(false);
        goTo(i);
        if (cur === i) { var v = main(i); if (v) play(v); }
        slides.focus({ preventScroll: true });
      });
    });

    modal.addEventListener('close', function () {
      modal.querySelectorAll('video').forEach(function (v) { if (!v.paused) v.pause(); });
      modal.classList.remove('ha-ugcm--playing');
      cur = -1;
    });

    boxes.forEach(function (box, i) {
      box.addEventListener('click', function () { goTo(i, 'smooth'); });
    });

    modal.querySelectorAll('[data-ha-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(cur + (parseInt(btn.dataset.haStep, 10) || 1), 'smooth');
      });
    });

    function toggle() {
      var v = main(cur);
      if (!v) return;
      if (v.paused) play(v); else v.pause();
    }

    var ppBtn = modal.querySelector('[data-ha-pp]');
    if (ppBtn) ppBtn.addEventListener('click', toggle);

    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        setMuted(!muted);
        var v = main(cur);
        if (!muted && v && v.paused) play(v);
      });
    }

    var shareBtn = modal.querySelector('[data-ha-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var url = location.href.split('#')[0];
        if (navigator.share) {
          navigator.share({ title: document.title, url: url }).catch(noop);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () { toast('Copied link'); }, noop);
        }
      });
    }

    slides.addEventListener('click', function (e) {
      if (e.target.classList.contains('ha-ugcm__video')) toggle();
    });

    // play / pause / ended / error don't bubble — listen in the capture phase
    slides.addEventListener('play', function (e) {
      if (e.target === main(cur)) modal.classList.add('ha-ugcm--playing');
    }, true);
    slides.addEventListener('pause', function (e) {
      if (e.target === main(cur)) modal.classList.remove('ha-ugcm--playing');
    }, true);
    slides.addEventListener('ended', function (e) {
      if (e.target === main(cur)) goTo(cur + 1, 'smooth');
    }, true);

    // a video the browser can't decode (e.g. an HEVC upload) drops out of the
    // playlist — hiding the slide takes it out of the snap flow for free
    function drop(v) {
      var slide = v.closest('.ha-ugcm__slide');
      var i = slideEls.indexOf(slide);
      if (i < 0 || slide.hidden) return;
      slide.hidden = true;
      if (boxes[i]) boxes[i].hidden = true;
      if (i === cur) { cur = -1; goTo(i + 1, 'smooth'); }
    }
    slides.addEventListener('error', function (e) {
      if (e.target.classList.contains('ha-ugcm__video')) drop(e.target);
    }, true);
    slides.addEventListener('loadeddata', function (e) {
      if (e.target.classList.contains('ha-ugcm__video') && !e.target.videoWidth) drop(e.target);
    }, true);

    // mandatory snap swallows the small scroll an arrow key makes
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); goTo(cur + 1, 'smooth'); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); goTo(cur - 1, 'smooth'); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
